import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import Button from '../../components/common/Button';
import { DocumentViewer } from '../../components/DocumentViewer';
import { documentRepo } from '../../data/db';
import { documentLibraryApi, extractOne } from '../../api/facades';
import type { Document, User } from '../../types';

const DocumentDetailPage: React.FC = () => {
  const { documentId } = useParams();
  const demoDoc = useMemo(() => (documentId ? documentRepo.get(documentId) : undefined), [documentId]);

  // Document Library fallback (numeric id).
  const fileId = documentId ? Number(documentId) : NaN;

  const {
    data: fileRes,
    isLoading: fileLoading,
    isError: fileError,
  } = useQuery({
    queryKey: ['document-library-file', fileId],
    queryFn: () => documentLibraryApi.getFile(fileId),
    enabled: !Number.isNaN(fileId) && !demoDoc,
  });

  const file = useMemo(() => extractOne(fileRes), [fileRes]);

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blobError, setBlobError] = useState<string | null>(null);
  const [blobLoading, setBlobLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!file || Number.isNaN(fileId)) return;
      setBlobLoading(true);
      setBlobError(null);
      setBlobUrl(null);

      try {
        const res: AxiosResponse<Blob> = await documentLibraryApi.downloadFile(fileId);
        const blob = res.data;
        const url = URL.createObjectURL(blob);
        if (cancelled) return;
        setBlobUrl(url);
      } catch (e) {
        if (cancelled) return;
        setBlobError(e instanceof Error ? e.message : 'Failed to load file');
      } finally {
        if (cancelled) return;
        setBlobLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [file?.id, fileId]);

  if (!documentId) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200 mb-2">Document not found</h1>
        <Link to="/dashboard/documents">
          <Button variant="primary">Back to Documents</Button>
        </Link>
      </div>
    );
  }

  if (demoDoc) {
    return (
      <div className="-m-6 -mx-6 xl:-mx-0">
        <DocumentViewer document={demoDoc} />
      </div>
    );
  }

  if (Number.isNaN(fileId) || fileError) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400">Document not found.</p>
        <Link to="/dashboard/documents">
          <Button variant="primary" className="mt-4">Back to Documents</Button>
        </Link>
      </div>
    );
  }

  const placeholderUser: User = {
    id: 'document-library-user',
    username: 'Document Library',
    email: '',
    initials: 'DL',
  };

  const doc: Document | null =
    file && blobUrl
      ? {
          id: String(fileId),
          name: file.name ?? 'Document',
          type: file.mime_type ?? 'document',
          uploadedBy: placeholderUser,
          uploadedAt: file.created_at ?? new Date().toISOString(),
          size: file.size ?? undefined,
          url: blobUrl,
        }
      : null;

  if (fileLoading || blobLoading || (!file && !blobError)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-500 dark:text-slate-400 text-sm">Loading…</p>
      </div>
    );
  }

  if (blobError) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">{blobError}</p>
        <Link to="/dashboard/documents">
          <Button variant="secondary">Back to Documents</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="-m-6 -mx-6 xl:-mx-0">
      {doc ? (
        <DocumentViewer document={doc} backHref="/dashboard/documents" editHref={null} onDelete={null} />
      ) : (
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <p className="text-slate-600 dark:text-slate-400">Document preview unavailable.</p>
          <Link to="/dashboard/documents">
            <Button variant="secondary" className="mt-4">
              Back to Documents
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default DocumentDetailPage;
